import { Object3D, Vector3 } from 'three'
import LayerKeys from '~/config/layerKeys'
import { RootState } from '~/store'
import { useAppDispatch, useAppSelector, useAppStore } from '~/store/hooks'
import { getThreeHexFromTheme } from '~/lib/utils'
import { selectObjects } from '~/store/selectedObjects'
import { Edges } from '@react-three/drei'
import { useEffect, useRef, useState, useTransition } from 'react'
import { updateDoor } from '~/store/objects'
import ObjectClassName from '~/config/objectClassNames'
import { orthogonalVectorOfWallSegment, parallelVectorOfWallSegment } from '../util/walls'
import { Door } from '~/store/objects/types'
import { useWallSegmentSnapLines } from '../hooks'
import { snapToLine } from '../util/snaplines'
import { modelToUI, uiToModel, vectorUIToModel } from '../util/units'
import { SnapPreview } from './SnapPreview'
import { DoorArrows } from './DoorArrows'
import { SnapLine } from '../util/snaplineVisuals'
import { DoorProduct } from '~/components/DrawingCanvas/Doors/DoorProduct'
import { DragControls } from '../util/DragControls'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'
import { selectDenormalizedWalls } from '~/store/objects/selectors/walls'

const Z_FIGHTING_PREVENTION_EPSILON = 0.25

export function Doors() {
  const isVisible = useAppSelector(it => it.layers.layers[LayerKeys.DOORS].visible)
  const interiorWallsVisible = useAppSelector(it => it.layers.layers[LayerKeys.INTERIOR_WALLS].visible)
  const exteriorWallsVisible = useAppSelector(it => it.layers.layers[LayerKeys.EXTERIOR_WALLS].visible)
  const doors = useAppSelector(it => it.objects.present.doors)
  if (!isVisible) {
    return <></>
  }

  return (
    <group name={LayerKeys.DOORS}>
      {Object.entries(doors).map(([key, value]) => (
        <ADoor key={key} door={value} {...{ interiorWallsVisible, exteriorWallsVisible, isVisible }} />
      ))}
    </group>
  )
}

function RollbackDoorClearance(props: { door: RootState['objects']['present']['doors'][string]; thickness: number }) {
  const is3D = useAppSelector(s => s.camera.is3D)
  const nativeHeight = modelToUI(props.door.height)
  const doorWidth = modelToUI(props.door.width)
  const offset = Math.abs(nativeHeight / 2)

  return (
    <mesh position={[offset * -1, 0, offset]}>
      <boxGeometry args={[doorWidth, doorWidth, props.thickness / 2]} />
      <meshLambertMaterial
        color={getThreeHexFromTheme('three.objects.door.border')}
        transparent={true}
        opacity={is3D ? 1 : 0}
      />
      <Edges linewidth={1} color={getThreeHexFromTheme('three.objects.door.border')} />
    </mesh>
  )
}
function VerticalDoorClearance(props: { door: RootState['objects']['present']['doors'][string]; thickness: number }) {
  const is3D = useAppSelector(s => s.camera.is3D)
  const nativeHeight = modelToUI(props.door.height)
  const doorWidth = modelToUI(props.door.width)
  const offset = Math.abs(props.thickness / 2)

  return (
    <mesh position={[offset * -1, 0, nativeHeight]}>
      <boxGeometry args={[props.thickness / 2, doorWidth, nativeHeight]} />
      <meshLambertMaterial
        color={getThreeHexFromTheme('three.objects.door.border')}
        transparent={true}
        opacity={is3D ? 1 : 0}
      />
      <Edges linewidth={1} color={getThreeHexFromTheme('three.objects.door.border')} />
    </mesh>
  )
}
function ADoor(props: {
  door: Door
  interiorWallsVisible: boolean
  exteriorWallsVisible: boolean
  isVisible: boolean
}) {
  const {
    position: { x, y },
    width,
    height,
    wallSegmentId,
    doorType,
    id,
  } = props.door
  const denormalizedSegments = useAppSelector(selectDenormalizedWalls).map(({ segments }) => segments)
  const wallSegment = denormalizedSegments.flat().find(({ id }) => id === wallSegmentId)!
  const dispatch = useAppDispatch()
  const selected = useAppSelector(it => it.selectedObjects.some((sel: { id: string }) => sel.id === id))
  const is3D = useAppSelector(it => it.camera.is3D)
  const [isDragging, setDragging] = useState(false)
  const snaplines = useWallSegmentSnapLines()
  const [deltaAccumulator, setDeltaAccumulator] = useState([0, 0] as [number, number])
  const [, startTransition] = useTransition()
  const snapPoint = isDragging
    ? snapToLine(snaplines, new Vector3(modelToUI(x) + deltaAccumulator[0], modelToUI(y) + deltaAccumulator[1], 0))
    : undefined
  const objectRef = useRef<Object3D>(null!)

  const thickness = wallSegment.thickness

  const store = useAppStore()
  const doorX = modelToUI(x)
  const doorY = modelToUI(y)
  const doorZ = modelToUI(height) / 2

  const doorWidth = modelToUI(width)
  const doorHeight = modelToUI(height)
  const doorThickness = modelToUI(thickness) + Z_FIGHTING_PREVENTION_EPSILON

  const color = isDragging
    ? getThreeHexFromTheme('three.valid')
    : selected
      ? getThreeHexFromTheme('three.objects.door.selected')
      : getThreeHexFromTheme('three.objects.door.default')

  const rotation = orthogonalVectorOfWallSegment(wallSegment)

  const visible =
    props.isVisible &&
    !(wallSegment.layerKey === LayerKeys.EXTERIOR_WALLS && !props.exteriorWallsVisible) &&
    !(wallSegment.layerKey === LayerKeys.INTERIOR_WALLS && !props.interiorWallsVisible)

  useEffect(() => {
    const controller = new AbortController()
    let doDrag = false

    window.addEventListener(
      'Walls/startDrag',
      ({ detail }) => {
        doDrag = wallSegmentId === detail.segment
      },
      { signal: controller.signal }
    )
    window.addEventListener(
      'Walls/drag',
      ({ detail }) => {
        if (doDrag) {
          objectRef.current.position.copy(detail.delta)
        }
      },
      { signal: controller.signal }
    )
    window.addEventListener(
      'Walls/stopDrag',
      () => {
        if (!doDrag) {
          return
        }
        doDrag = false
      },
      { signal: controller.signal }
    )
    return () => controller.abort()
  }, [wallSegmentId, id, x, y])

  useEffect(() => {
    objectRef.current.position.set(0, 0, 0)
  }, [doorX, doorY, doorZ])

  return (
    <object3D
      ref={objectRef}
      visible={visible}
      onClick={handleClick(() => {
        dispatch(selectObjects({ objects: [{ ...props.door, className: ObjectClassName.DOOR }] }))
      })}
    >
      {selected && !isDragging && <DoorArrows door={props.door} wallSegment={wallSegment} />}
      <DragControls
        onDragStart={() => {
          setDragging(true)
        }}
        onDrag={(_, delta) => {
          startTransition(() => {
            setDeltaAccumulator([delta.x, delta.y])
          })
        }}
        onDragEnd={() => {
          setDragging(false)
          if (snapPoint !== undefined) {
            const wallSegment = store.getState().objects.present.segments[snapPoint.objectID]
            const rotation = parallelVectorOfWallSegment(wallSegment)
            dispatch(
              updateDoor({
                door: {
                  id,
                  position: { x: uiToModel(snapPoint.snapPoint.x), y: uiToModel(snapPoint.snapPoint.y), z: 0 },
                  wallSegmentId: snapPoint.objectID,
                  rotation: { _x: rotation.x, _y: rotation.y, _z: rotation.z },
                },
              })
            )
          }
          setDeltaAccumulator([0, 0])
        }}
      >
        <group visible={!isDragging}>
          {props.door.product ? <DoorProduct door={{ ...props.door, product: props.door.product }} /> : null}
          <mesh position={[doorX, doorY, doorZ]} rotation={rotation}>
            <boxGeometry args={[doorThickness, doorWidth, doorHeight]} />
            <meshLambertMaterial color={color} />
          </mesh>
          {!is3D && (
            <mesh position={[doorX, doorY, modelToUI(wallSegment.height)]} rotation={rotation}>
              <boxGeometry args={[doorThickness, doorWidth, 1]} />
              <meshLambertMaterial color={color} />
            </mesh>
          )}
        </group>
      </DragControls>
      {snapPoint && (
        <>
          <SnapPreview snapPoint={snapPoint} door={props.door} />
          {props.door.product ? (
            <DoorProduct
              door={{ ...props.door, product: props.door.product, position: vectorUIToModel(snapPoint.snapPoint) }}
            />
          ) : null}
        </>
      )}
      {snapPoint && <SnapLine snapPoint={snapPoint} />}
      <object3D
        position={[doorX, doorY, doorZ]}
        rotation={rotation}
        visible={!isDragging && doorType === 'rollbackDoor'}
      >
        <RollbackDoorClearance door={props.door} thickness={doorThickness} />
      </object3D>
      <object3D
        position={[doorX, doorY, doorZ]}
        rotation={rotation}
        visible={!isDragging && doorType === 'verticalDoor'}
      >
        <VerticalDoorClearance door={props.door} thickness={doorThickness} />
      </object3D>
    </object3D>
  )
}
